import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";

// Derive language deterministically from the URL. Reading sessionStorage at
// render time (as this hook used to) caused hydration mismatches because the
// pre-rendered HTML and the client's initial render disagreed on which lang
// was "current". URL is the single source of truth: any path starting with
// /ar (as a full segment) is Arabic, everything else is English. (Bare
// English is canonical on this app; /en/* is deliberately 404'd in Routes.)
const detectLang = (pathname, params) => {
  if (params?.lang === "ar") return "ar";
  return /^\/ar(\/|$)/.test(pathname) ? "ar" : "en";
};

export const useLang = () => {
  const params = useParams();
  const location = useLocation();
  const lang = detectLang(location.pathname, params);

  // Sync sessionStorage so any legacy code still reading it out-of-band
  // sees the right value. This runs after commit, so it can't affect
  // render output and can't cause hydration mismatches.
  useEffect(() => {
    try {
      window.sessionStorage.setItem("lang", lang);
    } catch (_) {
      /* private mode / SSR — safe to ignore */
    }
  }, [lang]);

  const setLang = (newLang) => {
    try {
      window.sessionStorage.setItem("lang", newLang);
      window.sessionStorage.setItem("langSwitching", "true");
    } catch (_) {}

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('languageChangeStart'));
    }

    let pathname = window.location.pathname;

    if (pathname.startsWith('/ar/') || pathname === '/ar') {
      pathname = pathname.replace(/^\/ar\/?/, '/');
    } else if (pathname.startsWith('/en/') || pathname === '/en') {
      pathname = pathname.replace(/^\/en\/?/, '/');
    }

    if (!pathname.startsWith('/')) {
      pathname = '/' + pathname;
    }

    const newPath = newLang === 'en'
      ? (pathname === '/' ? '/' : pathname)
      : `/${newLang}${pathname === '/' ? '' : pathname}`;
    window.location.href = window.location.origin + newPath + window.location.search + window.location.hash;
  };

  return { lang, setLang };
};
